import { useState, useRef } from "react";
import { X, Paperclip, CheckCircle2, ChevronRight } from "lucide-react";
import api from "../utils/api";

const CATEGORIES = [
  { key: "Bug",             emoji: "🐛", label: "Bug",             hint: "Something broke or isn't working"   },
  { key: "UI Issue",        emoji: "🎨", label: "UI Issue",        hint: "Layout, display or visual problem"  },
  { key: "Feature Request", emoji: "💡", label: "Suggestion",      hint: "An idea or improvement you'd like"  },
  { key: "Other",           emoji: "💬", label: "Other",           hint: "Anything else on your mind"         },
];

const MAX_CHARS = 2000;

function compressImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const MAX = 1200;
        let { width, height } = img;
        if (width > MAX) { height = Math.round((height * MAX) / width); width = MAX; }
        if (height > MAX) { width = Math.round((width * MAX) / height); height = MAX; }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function FeedbackModal({ onClose }) {
  const [step,        setStep]        = useState("form"); // "form" | "done"
  const [category,    setCategory]    = useState("Bug");
  const [description, setDescription] = useState("");
  const [imageData,   setImageData]   = useState(null);
  const [imageName,   setImageName]   = useState("");
  const [dragOver,    setDragOver]    = useState(false);
  const [submitting,  setSubmitting]  = useState(false);
  const [error,       setError]       = useState("");
  const fileRef = useRef();

  async function handleFile(file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) { setError("Only image files are allowed."); return; }
    if (file.size > 8 * 1024 * 1024)    { setError("Image must be under 8 MB."); return; }
    setError("");
    try {
      setImageData(await compressImage(file));
      setImageName(file.name);
    } catch {
      setError("Couldn't process that image — try a different file.");
    }
  }

  async function handleSubmit() {
    if (!description.trim()) { setError("Please describe what happened."); return; }
    setError(""); setSubmitting(true);
    try {
      await api.post("/api/feedback", {
        category,
        description: description.trim(),
        imageBase64: imageData || null,
        imageName:   imageName || null,
      });
      setStep("done");
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't send right now — please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // ── Shared styles ──────────────────────────────────────────────────────────
  const S = {
    overlay: {
      position: "fixed", inset: 0, zIndex: 3000,
      background: "rgba(2,6,23,0.75)", backdropFilter: "blur(6px)",
      display: "flex", alignItems: "flex-end", justifyContent: "center",
      padding: "0 0 0 0",
    },
    sheet: {
      width: "100%", maxWidth: 480,
      background: "#0F172A",
      borderTop: "1px solid rgba(255,255,255,0.07)",
      borderRadius: "20px 20px 0 0",
      boxShadow: "0 -24px 80px rgba(0,0,0,0.6)",
      overflow: "hidden",
      animation: "slideUp 0.25s cubic-bezier(0.32,0.72,0,1)",
    },
  };

  if (step === "done") return (
    <div style={S.overlay} onClick={onClose}>
      <style>{`@keyframes slideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}`}</style>
      <div style={{ ...S.sheet, padding: "40px 28px 48px", textAlign: "center" }} onClick={e => e.stopPropagation()}>
        <div style={{
          width: 56, height: 56, borderRadius: "50%",
          background: "rgba(34,197,94,0.12)", border: "1.5px solid rgba(34,197,94,0.3)",
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 20px",
        }}>
          <CheckCircle2 size={26} color="#22C55E" />
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, color: "#F1F5F9", marginBottom: 10 }}>
          Got it, thanks!
        </div>
        <div style={{ fontSize: 14, color: "#64748B", lineHeight: 1.7, marginBottom: 32 }}>
          We've received your feedback and will look into it.
        </div>
        <button onClick={onClose} style={{
          width: "100%", padding: "13px", borderRadius: 12,
          background: "#1E293B", border: "1px solid rgba(255,255,255,0.08)",
          color: "#94A3B8", fontSize: 14, fontWeight: 600, cursor: "pointer",
        }}>Close</button>
      </div>
    </div>
  );

  const selected = CATEGORIES.find(c => c.key === category);

  return (
    <div style={S.overlay} onClick={onClose}>
      <style>{`
        @keyframes slideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}
        .fb-area:focus{outline:none;border-color:rgba(99,102,241,0.5)!important;box-shadow:0 0 0 3px rgba(99,102,241,0.08)}
      `}</style>

      <div style={S.sheet} onClick={e => e.stopPropagation()}>
        {/* Drag handle */}
        <div style={{ padding: "12px 0 0", display: "flex", justifyContent: "center" }}>
          <div style={{ width: 36, height: 4, borderRadius: 99, background: "rgba(255,255,255,0.12)" }} />
        </div>

        {/* Header */}
        <div style={{ padding: "16px 22px 0", display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#F1F5F9", letterSpacing: "-0.3px" }}>
              Share feedback
            </div>
            <div style={{ fontSize: 13, color: "#475569", marginTop: 3 }}>
              Help us make LorryKhojo better
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "rgba(255,255,255,0.05)", border: "none",
            borderRadius: 8, width: 32, height: 32,
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", color: "#64748B", marginTop: 2,
          }}><X size={16} /></button>
        </div>

        {/* Body */}
        <div style={{ padding: "20px 22px 28px", display: "flex", flexDirection: "column", gap: 20 }}>

          {/* Category grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {CATEGORIES.map(c => {
              const active = category === c.key;
              return (
                <button
                  key={c.key} type="button"
                  onClick={() => setCategory(c.key)}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "11px 14px", borderRadius: 12, cursor: "pointer",
                    textAlign: "left", transition: "all 0.15s",
                    background: active ? "rgba(99,102,241,0.12)" : "rgba(255,255,255,0.03)",
                    border: active ? "1.5px solid rgba(99,102,241,0.45)" : "1.5px solid rgba(255,255,255,0.06)",
                  }}
                >
                  <span style={{ fontSize: 20, lineHeight: 1 }}>{c.emoji}</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: active ? "#818CF8" : "#CBD5E1", lineHeight: 1.2 }}>
                      {c.label}
                    </div>
                    <div style={{ fontSize: 10, color: active ? "#6366F1" : "#374151", marginTop: 2, lineHeight: 1.3 }}>
                      {c.hint}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Description */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#94A3B8" }}>
                {selected?.key === "Bug"             ? "What went wrong?" :
                 selected?.key === "UI Issue"        ? "What looks off?"  :
                 selected?.key === "Feature Request" ? "What's your idea?" :
                                                       "What's on your mind?"}
              </span>
              <span style={{ fontSize: 11, color: description.length > MAX_CHARS * 0.85 ? "#F87171" : "#374151" }}>
                {description.length}/{MAX_CHARS}
              </span>
            </div>
            <textarea
              className="fb-area"
              value={description}
              onChange={e => setDescription(e.target.value.slice(0, MAX_CHARS))}
              placeholder={
                selected?.key === "Bug"             ? "e.g. When I click Track on vehicle MH12AB1234, the page shows an error instead of the map location…" :
                selected?.key === "UI Issue"        ? "e.g. The vehicle list overlaps the map on mobile when more than 5 vehicles are added…" :
                selected?.key === "Feature Request" ? "e.g. It would be helpful to export toll history as a PDF or Excel file…" :
                                                      "Tell us anything — no detail is too small."
              }
              rows={4}
              style={{
                width: "100%", boxSizing: "border-box",
                background: "rgba(255,255,255,0.03)",
                border: "1.5px solid rgba(255,255,255,0.07)",
                borderRadius: 12, padding: "12px 14px",
                color: "#E2E8F0", fontSize: 13, lineHeight: 1.65,
                resize: "none", fontFamily: "inherit",
                transition: "border-color 0.15s, box-shadow 0.15s",
              }}
            />
          </div>

          {/* Screenshot */}
          <div>
            {imageData ? (
              <div style={{
                position: "relative", borderRadius: 12, overflow: "hidden",
                border: "1.5px solid rgba(255,255,255,0.07)",
              }}>
                <img src={imageData} alt="screenshot" style={{ width: "100%", maxHeight: 160, objectFit: "cover", display: "block" }} />
                <div style={{
                  position: "absolute", inset: 0,
                  background: "linear-gradient(to top, rgba(0,0,0,0.65) 0%, transparent 50%)",
                }} />
                <div style={{
                  position: "absolute", bottom: 0, left: 0, right: 0,
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "10px 14px",
                }}>
                  <span style={{ fontSize: 11, color: "#CBD5E1", maxWidth: "80%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {imageName}
                  </span>
                  <button
                    type="button"
                    onClick={() => { setImageData(null); setImageName(""); }}
                    style={{
                      background: "rgba(0,0,0,0.5)", border: "1px solid rgba(255,255,255,0.15)",
                      borderRadius: 6, padding: "4px 10px",
                      color: "#94A3B8", fontSize: 11, cursor: "pointer",
                    }}
                  >Remove</button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
                style={{
                  width: "100%", padding: "11px 16px",
                  display: "flex", alignItems: "center", gap: 10,
                  background: dragOver ? "rgba(99,102,241,0.08)" : "rgba(255,255,255,0.02)",
                  border: `1.5px dashed ${dragOver ? "rgba(99,102,241,0.5)" : "rgba(255,255,255,0.1)"}`,
                  borderRadius: 12, cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                <Paperclip size={15} color="#475569" />
                <span style={{ fontSize: 13, color: "#475569" }}>
                  Attach a screenshot{" "}
                  <span style={{ color: "#374151" }}>— optional, up to 8 MB</span>
                </span>
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={e => handleFile(e.target.files[0])} />
          </div>

          {/* Error */}
          {error && (
            <div style={{
              fontSize: 13, color: "#FCA5A5",
              background: "rgba(239,68,68,0.08)",
              border: "1px solid rgba(239,68,68,0.18)",
              borderRadius: 10, padding: "10px 14px",
            }}>{error}</div>
          )}

          {/* Submit */}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !description.trim()}
            style={{
              width: "100%", padding: "14px",
              borderRadius: 12, border: "none",
              background: submitting || !description.trim() ? "rgba(99,102,241,0.35)" : "#6366F1",
              color: "#fff", fontSize: 14, fontWeight: 700,
              cursor: submitting || !description.trim() ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              transition: "background 0.15s",
              letterSpacing: "-0.1px",
            }}
          >
            {submitting ? "Sending…" : <>Send feedback <ChevronRight size={16} /></>}
          </button>

        </div>
      </div>
    </div>
  );
}
